import { cn } from "@/lib/utils";

export const BRAND = {
  logo: "/brand/flamingo-logo-web.png", // small copy of the logo for fast loading (original untouched)
  martin: "/brand/martin.webp",
  nasim: "/brand/nasim.webp",
  assistant: "/media/agent/base.webp",
};

const L = "/brand/landing/";
/** Real screenshots from the app: dish photos the assistant made, and exercise demo frames. */
export const DISH = {
  omelette: { src: `${L}dish-omelette.webp`, name: "Spinach, mushroom and feta omelette", kcal: 410 },
  bowl: { src: `${L}dish-chicken-bowl.webp`, name: "Chicken, quinoa and roast veg bowl", kcal: 620 },
  yogurt: { src: `${L}dish-yogurt.webp`, name: "Greek yogurt with berries and oats", kcal: 290 },
  salmon: { src: `${L}dish-salmon.webp`, name: "Salmon, sweet potato mash, broccoli", kcal: 580 },
};
export const EX = {
  benchM: { src: `${L}ex-bench-press-m.webp`, name: "Barbell bench press", part: "Chest" },
  dbBenchF: { src: `${L}ex-db-bench-f.webp`, name: "Dumbbell bench press", part: "Chest" },
  ohpM: { src: `${L}ex-overhead-press-m.webp`, name: "Seated overhead press", part: "Shoulders" },
  gobletF: { src: `${L}ex-goblet-squat-f.webp`, name: "Goblet squat", part: "Legs" },
  gobletM: { src: `${L}ex-goblet-squat-m.webp`, name: "Goblet squat", part: "Legs" },
  lungeM: { src: `${L}ex-lunge-m.webp`, name: "Dumbbell lunge", part: "Legs" },
  rdlF: { src: `${L}ex-rdl-f.webp`, name: "Romanian deadlift", part: "Glutes" },
  hipF: { src: `${L}ex-hip-thrust-f.webp`, name: "Barbell hip thrust", part: "Glutes" },
};

/** The ruled 1280px frame every section sits in (HubFit's vertical guide lines). */
export function Frame({ className, children, ...rest }) {
  return (
    <div
      className={cn("mx-auto max-w-frame border-x border-guide", className)}
      {...rest}
    >
      {children}
    </div>
  );
}

/** A full-width band with the horizontal rule on top. */
export function Band({ className, frameClassName, children, ...rest }) {
  return (
    <section className={cn("border-t border-line", className)} {...rest}>
      <Frame className={frameClassName}>{children}</Frame>
    </section>
  );
}

/** The name only (Ali: no logo mark anywhere on the site). The logo files stay untouched in /brand. */
export function Logo({ size = 32, className }) {
  return (
    <a href="/" aria-label="Fitamingo home" className={cn("flex items-center text-ink no-underline", className)} style={{ height: size }}>
      <span className="font-bold leading-none tracking-[-0.5px]" style={{ fontSize: Math.round(size * 0.72) }}>
        fitamingo
      </span>
    </a>
  );
}

const btnBase =
  "inline-flex h-[38px] items-center justify-center gap-1.5 whitespace-nowrap rounded-[10px] border px-5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2";

export function ButtonLink({ variant = "dark", className, children, ...rest }) {
  const look =
    variant === "dark"
      ? "border-black bg-black text-white hover:bg-neutral-800"
      : variant === "light"
        ? "border-neutral-200 bg-white text-ink hover:bg-neutral-50"
        : variant === "inverse"
          ? "border-white bg-white text-ink hover:bg-neutral-200"
          : "border-neutral-700 bg-transparent text-neutral-50 hover:bg-neutral-800";
  return (
    <a className={cn(btnBase, look, className)} {...rest}>
      {children}
    </a>
  );
}

/** Small icon tile + label that sits above section headings. */
export function Chip({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex h-5 w-5 items-center justify-center rounded-md bg-ink">
        <Icon className="h-3 w-3 text-lemon" strokeWidth={2.4} />
      </span>
      <span className="text-xs font-medium text-neutral-600">{children}</span>
    </div>
  );
}

const PILL = {
  neutral: "bg-neutral-100 text-neutral-600",
  dark: "bg-ink text-white",
  green: "bg-green-100 text-green-800",
  amber: "bg-amber-100 text-amber-800",
  lemon: "bg-[#FFFDE6] text-[#5B5B00]",
};

export function Pill({ tone = "neutral", className, children }) {
  return (
    <span
      className={cn(
        "inline-flex h-[22px] items-center whitespace-nowrap rounded-md px-2 text-xs font-medium",
        PILL[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const AV = {
  amber: "bg-amber-200 text-amber-900",
  blue: "bg-blue-200 text-blue-900",
  pink: "bg-pink-200 text-pink-900",
  violet: "bg-violet-200 text-violet-900",
  cyan: "bg-cyan-100 text-cyan-800",
  orange: "bg-orange-200 text-orange-900",
};

export function Initials({ text, tone = "amber", size = 28 }) {
  return (
    <span
      className={cn("flex shrink-0 items-center justify-center rounded-full font-bold", AV[tone])}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {text}
    </span>
  );
}

export function PhotoAvatar({ src, size = 28, className, alt = "" }) {
  return (
    <img
      src={src}
      alt={alt}
      className={cn("shrink-0 rounded-full border-2 border-white object-cover", className)}
      style={{ width: size, height: size, objectPosition: "50% 18%" }}
    />
  );
}

/** Section heading block (chip, h2, paragraph, buttons). */
export function SectionIntro({ icon, label, title, children, cta, center = false }) {
  return (
    <div
      data-reveal
      className={cn(
        "flex flex-col px-4 pb-12 pt-16 sm:px-10 sm:pb-16 sm:pt-20",
        center && "items-center text-center",
      )}
    >
      <Chip icon={icon}>{label}</Chip>
      <h2 className="mt-3 max-w-[560px] text-pretty text-[34px] font-medium leading-[1.1] tracking-[-0.02em] sm:text-5xl sm:leading-[52px]">
        {title}
      </h2>
      <p className="mt-3.5 max-w-[600px] text-pretty text-base leading-7 text-neutral-500 sm:text-lg">
        {children}
      </p>
      {cta}
    </div>
  );
}
